import React, { useState } from 'react';
import { Box, Button, Paper, Typography, Alert, IconButton } from '@mui/material';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RichTextContent from '../../common/RichTextContent';
import { shuffle } from '../../../utils/shuffle';

interface SortingPlayerProps {
    lesson: any;
    onComplete: () => void;
    onProgressUpdate?: (lessonId: number, progressData: {
        status?: string;
        progressPercentage?: number;
        score?: string;
        timeSpentSeconds?: number;
        suspendData?: any;
    }) => Promise<boolean>;
}

const SortingPlayer: React.FC<SortingPlayerProps> = ({ lesson, onComplete, onProgressUpdate }) => {
    const content = lesson.content || {};
    const { instruction, items = [], learningContent = '' } = content;

    const itemKey = items.map((item: any) => `${item.id}:${item.text}`).join('|');

    const [order, setOrder] = useState(() => ({ key: itemKey, items: shuffle<any>(items) }));
    const [checked, setChecked] = useState(false);

    if (order.key !== itemKey) {
        setOrder({ key: itemKey, items: shuffle<any>(items) });
        setChecked(false);
    }

    const sortedItems = order.items;
    const minScore = lesson.minScore || 70;

    const moveItem = (index: number, direction: 'up' | 'down') => {
        const next = [...sortedItems];
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
        setOrder({ key: itemKey, items: next });
    };

    const isAtCorrectPosition = (item: any, index: number) => item.position === index + 1;

    const correctCount = sortedItems.filter(isAtCorrectPosition).length;
    const score = sortedItems.length === 0
        ? 100
        : Math.round((correctCount / sortedItems.length) * 100);
    const passed = score >= minScore;

    const handleComplete = async () => {
        if (onProgressUpdate) {
            await onProgressUpdate(lesson.id, {
                score: String(score),
                status: 'completed',
                progressPercentage: 100,
            });
        }
        onComplete();
    };

    return (
        <Box>
            {learningContent && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <RichTextContent html={learningContent} />
                </Paper>
            )}

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    {instruction || 'Bringen Sie die Elemente in die richtige Reihenfolge'}
                </Typography>

                {sortedItems.map((item: any, index: number) => {
                    const correct = isAtCorrectPosition(item, index);

                    return (
                        <Box key={item.id} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                            <Typography sx={{ width: 30 }}>{index + 1}.</Typography>
                            <Paper
                                sx={{
                                    p: 2,
                                    flex: 1,
                                    ...(checked && {
                                        border: 2,
                                        borderColor: correct ? 'success.main' : 'error.main',
                                    }),
                                }}
                            >
                                <Typography>{item.text}</Typography>
                                {checked && !correct && (
                                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                        {`Richtige Position: ${item.position}`}
                                    </Typography>
                                )}
                            </Paper>
                            {checked && (correct
                                ? <CheckCircleIcon color="success" titleAccess="Richtig einsortiert" />
                                : <CancelIcon color="error" titleAccess="Falsch einsortiert" />
                            )}
                            <Box>
                                <IconButton
                                    size="small"
                                    aria-label="Nach oben"
                                    onClick={() => moveItem(index, 'up')}
                                    disabled={index === 0}
                                >
                                    <ArrowUpward />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    aria-label="Nach unten"
                                    onClick={() => moveItem(index, 'down')}
                                    disabled={index === sortedItems.length - 1}
                                >
                                    <ArrowDownward />
                                </IconButton>
                            </Box>
                        </Box>
                    );
                })}

                {checked && (
                    <Alert severity={passed ? 'success' : 'warning'} sx={{ mt: 3 }}>
                        {passed
                            ? `Ergebnis: ${score}% — bestanden.`
                            : `Ergebnis: ${score}% (mindestens ${minScore}% nötig). Grün markierte Elemente stehen richtig, die rot markierten verschieben Sie an die angezeigte Position.`}
                    </Alert>
                )}
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                {!checked ? (
                    <Button variant="contained" onClick={() => setChecked(true)}>Absenden</Button>
                ) : (
                    <Button variant="contained" onClick={handleComplete} disabled={!passed}>
                        Bestätigen
                    </Button>
                )}
            </Box>
        </Box>
    );
};

export default SortingPlayer;
